/**
 * What a skill should look like when it is used.
 *
 * Still no per-skill renderer: this reads the properties the skill already has —
 * its type, its violation, its range, how many cells it covers, whether it points
 * at anyone — and picks a DELIVERY, which is the grammar of the action rather than
 * the identity of the spell. A rifle shot and a bolt of force are the same delivery
 * with different violations; a fireball and an ice storm are the same delivery at
 * different sizes.
 *
 * The one thing that is genuinely per-skill is the sprite: the skill's own icon,
 * carried on the projectile and stamped at the impact, because that is the picture
 * the player already associates with the button they pressed.
 */

import { normalizeViolationType, isStatNameNotViolation } from './violation';
import { skillTargeting, sprayCellCount } from './aim';

function skillOf(ability) {
  if (!ability) return null;
  return ability.spray_config || ability.impact ? ability : (ability.item?.skill || ability.skill || null);
}

/**
 * The second axis: what the action is DOING.
 *
 * This axis is not invented here — the domain already carries it. Every entry in a
 * skill's `impact` list has BOTH `kind` (ImpactType: Damage, Heal, Shield, Buff,
 * Debuff, Burn, Fear, Lifesteal…) and `type` (ImpactViolationType: the material).
 * The action log carries the same pair per resolved impact. So the renderer reads
 * the pair the game already resolved rather than guessing from a skill's label.
 *
 * ImpactViolationType says what an effect is made of — heat, cold, flow, dark. It
 * cannot say whether that material is being used to wound someone or to mend them,
 * and most of the game is not attacks. A heal, a ward, a blessing and a curse all
 * carry violation `None`, so keyed on violation alone every supportive action in
 * the game renders as the same grey nothing.
 *
 * So intent is read off the skill's own type and carries the things violation
 * cannot: which way the effect MOVES (a mend rises and gathers, an affliction sinks
 * and drains), what it leaves on the ground, and a colour it pulls toward. The two
 * axes compose — a Heat ward is still hot, and still a ward.
 *
 *   lift      vertical bias of everything that moves: up is given, down is taken
 *   converge  +1 draws inward to the subject, -1 throws outward from it
 *   sigil     the mark left on the ground, or none
 *   bias      the colour the violation is pulled toward, and how far
 */
export const INTENTS = {
  harm: {
    index: 0, lift: -0.1, converge: -1.0, sigil: null, bias: null, biasAmount: 0,
  },
  mend: {
    index: 1, lift: 1.0, converge: 0.75, sigil: 'mend', bias: '#8FE9A8', biasAmount: 0.55,
  },
  bolster: {
    index: 2, lift: 0.65, converge: 0.35, sigil: 'ward', bias: '#FFD27A', biasAmount: 0.52,
  },
  afflict: {
    index: 3, lift: -0.7, converge: 0.9, sigil: 'hex', bias: '#6E5F86', biasAmount: 0.5,
  },
  reveal: {
    index: 4, lift: 0.2, converge: -0.4, sigil: 'scan', bias: '#9FD8E8', biasAmount: 0.35,
  },
  /*
   * A ward is not a buff. A buff is a change to someone; a ward is a THING placed
   * between them and the world, and it is keyed to a violation — `Shield`'s primary
   * key IS an ImpactViolationType — so a fire ward should look like fire held in a
   * shape rather than like a generic bubble.
   */
  guard: {
    index: 5, lift: 0.3, converge: 0.2, sigil: 'ward', bias: '#BFD8FF', biasAmount: 0.3,
  },
};

/**
 * ImpactType to intent.
 *
 * The enum in `dx_backend/apps/core/models/core.py` is the contract, and this map
 * is exhaustive over it — nothing else is a legal `impact[].kind`. An unmapped kind
 * silently falls back to `harm`, and a heal that renders as a wound is worse than no
 * effect at all, so a key that is NOT in the enum is as much a bug as a missing one:
 * it reads as coverage the renderer does not actually have.
 */
const INTENT_BY_IMPACT_KIND = {
  // Given.
  Heal: 'mend',
  Regeneration: 'mend',
  Lifesteal: 'harm',

  // Added.
  Buff: 'bolster',
  Haste: 'bolster',

  // Interposed.
  Shield: 'guard',
  Reflect: 'guard',
  Absorb: 'guard',
  Dodge: 'guard',
  Resist: 'guard',
  Immunity: 'guard',

  // Taken, in all its forms — stat theft, control, and the slow kinds of harm.
  Debuff: 'afflict',
  Stun: 'afflict',
  Sleep: 'afflict',
  Confusion: 'afflict',
  Paralysis: 'afflict',
  Fear: 'afflict',
  Freeze: 'afflict',
  Burn: 'afflict',
  Poison: 'afflict',
  Slow: 'afflict',
  Blind: 'afflict',
  Silence: 'afflict',
  Bleed: 'afflict',
  Disarm: 'afflict',
  Root: 'afflict',
  'Energy Decrease': 'afflict',

  // Done to.
  Damage: 'harm',
  'Knock out': 'harm',

  None: 'reveal',
};

/** The skill's own type, for anything with no impact list to read. */
const INTENT_BY_SKILL_TYPE = {
  ATTACK: 'harm',
  HEAL: 'mend',
  BUFF: 'bolster',
  DEFENSE: 'guard',
  DEBUFF: 'afflict',
  SUMMON: 'bolster',
  UTILITY: 'reveal',
  SPECIAL: 'reveal',
};

/**
 * What this action is doing, preferring what the game actually resolved.
 *
 * A resolved impact from the action log wins over the skill's declared type,
 * because a skill can carry several impacts and the one that landed is the one
 * worth showing. The skill type is the fallback for a preview, where nothing has
 * been resolved yet.
 */
function lookupIntent(kind) {
  if (!kind) return undefined;
  if (INTENT_BY_IMPACT_KIND[kind]) return INTENT_BY_IMPACT_KIND[kind];
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[impact/presentation] unmapped ImpactType "${kind}" fell back to skill type — add it to INTENT_BY_IMPACT_KIND.`);
  }
  return undefined;
}

/**
 * Intents that take something away. A supportive skill may never render as one of
 * these, however its impact list was authored.
 */
const TAKING_INTENTS = new Set(['harm', 'afflict']);

export function intentFor(ability, resolvedImpact = null) {
  const skill = skillOf(ability);
  const first = Array.isArray(skill?.impact) ? skill.impact[0] : null;
  const declared = INTENT_BY_SKILL_TYPE[String(skill?.type || '').toUpperCase()];

  const read = (
    lookupIntent(resolvedImpact?.kind || resolvedImpact?.type) ||
    lookupIntent(first?.kind)
  );

  /*
   * The impact list is not always honest about what the skill is FOR. "Tech-Enhanced
   * Agility" is a BUFF that carries a `Damage`/`Energy` impact — the number it moves
   * is the target's damage, not damage dealt to anyone — and read literally it renders
   * a buff as an attack: no lift, no mark on the ground, the caster's own ally lit up
   * like a wound. Where the skill's own category is unambiguously supportive it
   * outranks an impact that says something is being taken.
   */
  if (read && TAKING_INTENTS.has(read) && declared && !TAKING_INTENTS.has(declared)) {
    return declared;
  }

  return read || declared || 'harm';
}

/**
 * The materials a ward is raised against.
 *
 * `Shield`'s primary key IS an ImpactViolationType, and a skill may list several —
 * "Reinforced Shielding" and "Construct Sentinel" both carry all seven. Taking
 * `impact[0]` for those draws a ward against everything as a plain Energy bubble,
 * which is the arbitrary answer: the first row of a fixture decides what the shield
 * is made of. So a multi-violation ward is handed its whole list and the shell is
 * built from all of them at once — one material per plate — which is the only read
 * that says "against all of this" rather than "against Energy".
 */
export function wardSpectrum(ability, resolvedImpact = null) {
  /* A resolved impact is one violation actually landing; that is not a spectrum. */
  if (resolvedImpact?.violation) return [];
  const impacts = skillOf(ability)?.impact;
  if (!Array.isArray(impacts)) return [];
  const guarding = impacts.filter(item => INTENT_BY_IMPACT_KIND[item?.kind] === 'guard');
  const materials = [...new Set(
    guarding
      .map(item => item.type || item.violation)
      .filter(type => type && !isStatNameNotViolation(type))
      .map(normalizeViolationType)
      .filter(type => type !== 'None'),
  )];
  return materials.length > 1 ? materials : [];
}

/**
 * Lifesteal and absorption take something and CARRY IT BACK. Without the return
 * leg they read as ordinary damage, and the whole point of the mechanic is that
 * the caster gained what the target lost.
 */
export function returnsToCaster(ability, resolvedImpact = null) {
  const kind = resolvedImpact?.kind || resolvedImpact?.type
    || (Array.isArray(skillOf(ability)?.impact) ? skillOf(ability).impact[0]?.kind : null);
  return kind === 'Lifesteal' || kind === 'Absorb';
}

/**
 * The deliveries.
 *
 * `trace` is what crosses the ground between caster and target; `arrival` is what
 * happens when it gets there. A shot has almost no trace and a hard arrival; a ward
 * has no trace at all and grows where it stands.
 */
export const DELIVERIES = {
  shot: {
    trace: 'beam', traceMs: 130, arrival: 'flash', arrivalMs: 260,
    lunge: 0, casterKick: 0.55, targetShake: 1.0, spriteOnTrace: false,
  },
  projectile: {
    trace: 'orb', traceMs: 520, arrival: 'burst', arrivalMs: 900,
    lunge: 0, casterKick: 0.2, targetShake: 1.0, spriteOnTrace: true,
  },
  /*
   * A blow is two beats, not one: the caster commits, and the arc lands. The commit
   * is drawn as a `charge` — a low streak of light and grit thrown down the line the
   * attacker closes along. PlayerTacticalMap wires `onLunge` and does move the token
   * now, but the streak stays: the body covers at most `LUNGE_MAX_CELLS` of the gap,
   * and the light is what says the rest of it was crossed.
   */
  melee: {
    trace: 'charge', traceMs: 240, arrival: 'slash', arrivalMs: 480,
    lunge: 0.72, casterKick: 0.55, targetShake: 1.35, spriteOnTrace: false,
  },
  ward: {
    trace: 'none', traceMs: 0, arrival: 'dome', arrivalMs: 900,
    lunge: 0, casterKick: 0, targetShake: 0.15, spriteOnTrace: false,
  },
  /*
   * A blessing GATHERS; it is not dropped on anyone.
   *
   * This used to share `descend` with the hex, and a descending shaft is a column of
   * light punched through the subject and into the floor — on a one-cell heal that is
   * a hairline beam arriving from off-screen, which is a weapon firing, not a wound
   * closing. `INTENTS.mend` already says which way this moves (`lift 1.0`,
   * `converge 0.75`); the trace now says the same thing, so a heal is read from motes
   * drawn inward off the ground and lifted, and nothing crosses the subject.
   */
  bless: {
    trace: 'gather', traceMs: 760, arrival: 'column', arrivalMs: 900,
    lunge: 0, casterKick: 0, targetShake: 0.1, spriteOnTrace: true,
  },
  hex: {
    trace: 'descend', traceMs: 380, arrival: 'column', arrivalMs: 900,
    lunge: 0, casterKick: 0, targetShake: 0.35, spriteOnTrace: true,
  },
  field: {
    trace: 'none', traceMs: 0, arrival: 'burst', arrivalMs: 800,
    lunge: 0, casterKick: 0.1, targetShake: 0.4, spriteOnTrace: false,
  },
  /*
   * Support delivered at arm's length.
   *
   * `bless`, `ward` and `hex` above all stand still: the caster stays where they are
   * and the effect appears on its subject. That is right at a distance and wrong at
   * touch — a heal authored at range 1 is a hand put on someone, and drawing it as a
   * shaft of light arriving on a body two cells away makes the healer a spectator of
   * their own action. So the close-range forms borrow the blow's first beat: the
   * caster commits down the line (`charge`, and a real `lunge` the board moves the
   * token by) and the effect resolves the way its distant form does.
   *
   * They keep the distant form's arrival exactly, so a touch ward is still a ward and
   * a touch hex still lands as a hex — only the approach is different. Nothing here
   * shakes its subject the way a blow does: a hand arriving is not an impact.
   */
  reach: {
    trace: 'charge', traceMs: 300, arrival: 'column', arrivalMs: 860,
    lunge: 0.5, casterKick: 0.12, targetShake: 0.12, spriteOnTrace: true,
  },
  brace: {
    trace: 'charge', traceMs: 280, arrival: 'dome', arrivalMs: 900,
    lunge: 0.45, casterKick: 0.12, targetShake: 0.15, spriteOnTrace: false,
  },
  press: {
    trace: 'charge', traceMs: 300, arrival: 'column', arrivalMs: 880,
    lunge: 0.55, casterKick: 0.18, targetShake: 0.42, spriteOnTrace: true,
  },
  /*
   * The two ways a large area arrives.
   *
   * A wide effect has no plausible flight: an orb thrown flat across the board and
   * bursting into eighty cells reads as a marble that happened to explode. Something
   * that covers a quarter of the map came from somewhere the caster is not — usually
   * from above, which is `skyfall`: a shaft driven down onto the ground it claims,
   * the same `descend` a hex uses, resolving into the area's own burst.
   *
   * `upheaval` is the exception the model asks for — "likely from the sky, but not
   * always". A quake, a tide, a fissure comes from UNDER the ground, so it gathers
   * off the floor first and then breaks. Same size, opposite direction.
   */
  skyfall: {
    trace: 'descend', traceMs: 520, arrival: 'burst', arrivalMs: 950,
    lunge: 0, casterKick: 0.12, targetShake: 1.0, spriteOnTrace: true,
  },
  upheaval: {
    trace: 'gather', traceMs: 560, arrival: 'burst', arrivalMs: 950,
    lunge: 0, casterKick: 0.15, targetShake: 1.15, spriteOnTrace: false,
  },
  /*
   * Three actions that cross no ground at all.
   *
   * Every delivery above is an answer to "how did it get from the caster to the
   * target". These three have no such journey — nothing is thrown, swung or called
   * down — and rendering them as one anyway is why a dodge used to grow a dome and a
   * summoning used to resolve into a stain on a cell.
   *
   *   gather    the air comes IN to the subject and stays there
   *   displace  the subject is taken out of the world and put back
   *   summon    something on the other side is let through
   */
  gather: {
    trace: 'gather', traceMs: 1150, arrival: 'infall', arrivalMs: 900,
    lunge: 0, casterKick: 0, targetShake: 0.12, spriteOnTrace: false,
  },
  displace: {
    trace: 'collapse', traceMs: 430, arrival: 'reform', arrivalMs: 720,
    lunge: 0, casterKick: 0.15, targetShake: 0.2, spriteOnTrace: false,
  },
  summon: {
    trace: 'rift', traceMs: 640, arrival: 'landing', arrivalMs: 950,
    lunge: 0, casterKick: 0.1, targetShake: 0.55, spriteOnTrace: false,
  },
};

/**
 * The board's rules, versus the bench's.
 *
 * A fight is played on cells: what a skill hit has to be exactly what the server
 * said it hit, so the live board renders the strict cell union and never lets an
 * effect cross a wall. The bench defaults the other way because it exists to look
 * at the effect, not to adjudicate it.
 */
export const BATTLE_RENDER_DEFAULTS = {
  mergeMode: 'rounded-cells',
  clipToPassable: true,
};

export const SANDBOX_RENDER_DEFAULTS = {
  mergeMode: 'ideal-shape',
  clipToPassable: false,
};

export function skillViolation(ability, resolvedImpact = null) {
  /* A resolved impact names its own violation; the log calls that field `violation`. */
  if (resolvedImpact?.violation && !isStatNameNotViolation(resolvedImpact.violation)) {
    return normalizeViolationType(resolvedImpact.violation);
  }
  const skill = skillOf(ability);
  const impacts = skill?.impact;
  if (Array.isArray(impacts) && impacts.length) {
    const raw = impacts[0].type || impacts[0].violation;
    /*
     * A scaling stat (`Speed`, `Dodge`, `Accuracy`…) sometimes ends up in this
     * slot from older authoring — it names what the formula scales with, not what
     * the effect is made of. Read as `None` explicitly rather than falling
     * through `normalizeViolationType`'s generic unknown-string case, so a buff's
     * material comes from its intent (`INTENTS.bolster`'s own bias) instead of
     * looking like an accidentally-unmapped violation.
     */
    if (isStatNameNotViolation(raw)) return 'None';
    return normalizeViolationType(raw);
  }
  return 'None';
}

/**
 * Whether an attack is delivered by contact.
 *
 * Reach alone cannot answer this. `max_range` in the live skill book collapses into
 * three coarse buckets — 0-2, 0-5 and 2-8 — and the middle one holds "Baseball
 * Punch" and "Energy Bolt" side by side, so a range test on its own renders half the
 * game's blows as beams. Only the close bucket is trustworthy by itself.
 *
 * What does separate them is the language the content already uses: a skill whose
 * own name or description says it punches, kicks, slashes, rakes or swings a blade
 * is a blow however generously its range was authored, and nothing else in the book
 * describes itself that way. This reads authored text, not a table of skill ids —
 * nothing here names a skill, and a punch added tomorrow classifies itself.
 *
 * Every word here has to name a way of touching someone. `strike` did not: it is the
 * book's generic word for any attack at all, and it was routing ranged bolts —
 * "Blood Reaper", "Phantom Strike" at range 2-8, "Resonant Discharge" — into a lunge
 * across ground the caster never crosses.
 *
 * The two places the word can appear are not equally trustworthy, and the fixture
 * says exactly how they differ. A contact word in the NAME is what the skill is:
 * "Cryo Slash", "Right Hook", "Claw of the Lesser Pact" are blows however generously
 * their reach was authored. The same word in the DESCRIPTION is usually prose about
 * something else — "Hurl the legendary Small Hammer" is a throw, "punches a four-cell
 * corridor" is a lance beam, "bodies and claws" is a summoned flood. So a description
 * match is only believed at a blow's own reach, a name that says the weapon LEAVES
 * THE HAND overrides any contact word in it, and nothing that covers ground larger
 * than the cell it lands on ever lunges — a footprint is a place, not a swing.
 */
const CONTACT_WORDS = /(punch|kick|hook|slash|slice|cleave|claw|rake|blade|edge|fist|slam|hammer|smash|bash|swing|stab|thrust|bite|rend|gore|jab|uppercut|melee)/i;

/** It left the hand: whatever it is made of, the caster stays where they are. */
const RELEASE_WORDS = /(throw|thrown|hurl|toss|lob|launch|volley|barrage|salvo|fusillade)/i;

const NAME_CONTACT_MAX_RANGE = 6;
const DESCRIPTION_CONTACT_MAX_RANGE = 5;

/**
 * The ground the skill was AUTHORED over, not the ground this aim happened to keep.
 *
 * A wall can clip a nine-cell nova down to one resolved cell, and the skill is still
 * a nova — so the pattern is read first and the resolved count only ever raises it.
 */
function footprintCells(skill, cellCount) {
  return Math.max(sprayCellCount(skill?.spray_config?.spray) || 0, cellCount || 0);
}

/*
 * A sweep covers several cells and is still an arm's arc. A detonation the caster
 * stands in the middle of is not: three cells across in every direction is a blast
 * radius, and there is no swing that shape.
 */
const BLAST_CELLS = 9;

/*
 * An area nobody could have thrown.
 *
 * Twelve cells is where the footprint stops being a splash around a point of impact
 * and becomes a piece of the map — the authored 5x5s, the 9x9 tides — and where a
 * flat trajectory stops explaining anything about how it got there.
 */
const WIDE_CELLS = 12;

/*
 * Where a wide effect comes from.
 *
 * The model is "the huge area likely arrives from the sky, but not always", so the
 * sky is the default and this is the list of characters that contradict it. Every
 * word names something that is OF THE GROUND — water that swells, rock that breaks,
 * growth that pushes up — and a quake drawn falling out of the air is exactly the
 * mistake the exception exists to prevent.
 *
 * Read from the name only. Descriptions are prose about consequences ("the ground
 * shakes") and would drag half the book underground.
 */
const GROUND_WORDS = /(quake|tremor|seismic|fissure|rupture|erupt|volcan|geyser|tide|tidal|flood|deluge|swamp|mire|maelstrom|whirlpool|vortex|root|vine|thorn|bramble|spike|pillar|wall|earth|terra|ground|floor|abyss)/i;

/**
 * Whether the caster can actually close the gap this cast.
 *
 * The board moves a lunging body at most `LUNGE_MAX_CELLS` (2.2) of the way, so a
 * blow aimed further than that lands in empty air however the skill was authored.
 * Contact is therefore a property of THIS cast, not only of the skill — see
 * `deliveryFor`.
 */
const CLOSABLE_CELLS = 3;

/** Arm's length: the reaches at which support is handed over rather than sent. */
const TOUCH_MAX_RANGE = 2;

function isContactAttack(skill, maxRange, cellCount) {
  const footprint = footprintCells(skill, cellCount);

  /* The close bucket is trustworthy on its own — including the sweeps, which cover
     several cells but cross no ground to reach them. */
  if (maxRange <= 2) return footprint < BLAST_CELLS;

  if (footprint > 1) return false;

  const name = skill?.name || '';
  if (RELEASE_WORDS.test(name)) return false;
  if (CONTACT_WORDS.test(name)) return maxRange <= NAME_CONTACT_MAX_RANGE;
  return CONTACT_WORDS.test(skill?.description || '') && maxRange <= DESCRIPTION_CONTACT_MAX_RANGE;
}

/**
 * The three actions that are not deliveries.
 *
 * Each is read off a property the domain already carries wherever one exists, and
 * off the NAME only where it does not — never off the description, for exactly the
 * reason above: "Tech-Enhanced Agility" grants evasion and "Hummer Throw" returns
 * "through dimensional displacement", and neither is a character stepping out of
 * the world. A name is what the skill IS.
 */
const MOVEMENT_WORDS = /(dodge|evade|evasion|dash|blink|teleport|displace|sidestep|vanish|reposition)/i;

/**
 * Drawing in, rather than putting out.
 *
 * The structural half is the reliable one: a skill with no `impact` at all but with
 * `stat_modifiers` on its effects projects nothing anywhere — the whole change is to
 * its subject's own numbers — which is precisely a gathering and precisely what
 * "Velocity Core" and "Etherion Core" are. The vocabulary catches the ones authored
 * the other way round, and is confined to buffs and specials so that a future
 * "Core Breach" cannot become a self-buff on the strength of one word.
 */
const GATHERING_WORDS = /(accumulat|gather|channel|attune|reservoir|\bcore\b)/i;

function impactsOf(skill) {
  return Array.isArray(skill?.impact) ? skill.impact : [];
}

function isSummoning(skill) {
  return String(skill?.type || '').toUpperCase() === 'SUMMON';
}

function isDisplacement(skill) {
  return MOVEMENT_WORDS.test(skill?.name || '');
}

function isGathering(skill, type) {
  if (type !== 'BUFF' && type !== 'SPECIAL') return false;
  if (GATHERING_WORDS.test(skill?.name || '')) return true;
  if (impactsOf(skill).length) return false;
  const effects = Array.isArray(skill?.effect) ? skill.effect : [];
  return effects.some(entry => Array.isArray(entry?.stat_modifiers) && entry.stat_modifiers.length);
}

/**
 * Which delivery an action is.
 *
 * Read in order of how strongly the property speaks. Reach decides melee before
 * anything else — a skill you have to stand on top of someone to use is a swing
 * whatever else it says about itself. After that the skill's own type decides
 * whether this is something done TO someone or FOR them, and only then does the
 * covered area separate a bolt from a bloom.
 *
 * REACH IS AN AXIS FOR EVERY TYPE, not only for attacks. A skill used at arm's
 * length involves the caster's body whatever it does with it — you step in to mend
 * someone, to shield them, to lay a curse on them — and reading the type first meant
 * reach never got a vote for anything but an attack, so every touch-range heal and
 * ward in the book resolved from across the board without anyone moving.
 *
 * `reach` (the option) is the gap this particular cast has to cover, when it is
 * known. It is not the skill's authored range: a blow authored at 0-5 is a blow at
 * one cell and cannot be one at five, because the body cannot get there — see
 * `CLOSABLE_CELLS`. Without it the authored range stands in, which is the right
 * answer for a preview that has no target yet.
 */
export function deliveryFor(ability, { cellCount = 1, reach = null } = {}) {
  const skill = skillOf(ability);
  if (!skill) return 'field';
  const type = String(skill.type || '').toUpperCase();
  const targeting = skillTargeting(skill);
  const maxRange = targeting?.maxRange ?? 5;
  const charges = targeting?.charges ?? 1;
  const footprint = footprintCells(skill, cellCount);

  /*
   * Asked before the type, because these three describe the GRAMMAR of the action
   * and the type only describes who benefits. A summon is not a buff being applied
   * to a cell and a dodge is not a shield being raised, so the type's answer would
   * be wrong before the area was even looked at.
   */
  if (isSummoning(skill)) return 'summon';
  if (isDisplacement(skill)) return 'displace';
  if (isGathering(skill, type)) return 'gather';

  /*
   * Touch, for the supportive types.
   *
   * Authored at arm's length AND landing on a body rather than on ground: a
   * one-or-two-cell effect at range 1-2 is a hand, a several-cell one at the same
   * range is a place and stands still. Range 0 is excluded on purpose — that is a
   * self-cast, and there is nobody to step towards.
   *
   * The cast's own gap has the final word where it is known: the same skill used on
   * the caster's own cell has no ground to close.
   */
  const touching = maxRange >= 1 && maxRange <= TOUCH_MAX_RANGE
    && footprint <= 2
    && (reach === null || (reach >= 1 && reach <= TOUCH_MAX_RANGE));

  if (type === 'DEFENSE') return touching ? 'brace' : 'ward';
  if (type === 'HEAL' || type === 'BUFF') return touching ? 'reach' : 'bless';
  if (type === 'DEBUFF') return touching ? 'press' : 'hex';

  if (type === 'ATTACK') {
    /*
     * A blow is a body arriving. Two things can disqualify one:
     *
     * the gap — beyond what a lunge can close the attacker never gets there, and a
     * punch that lands three cells from anybody is worse than the same skill read as
     * a shot; and the charge count — several charges are several separate arrivals,
     * and a body can only commit once, so a multi-charge "cascade" is a repeated
     * trace and not a repeated swing.
     */
    const closable = reach === null || reach <= CLOSABLE_CELLS;
    if (charges <= 1 && closable && isContactAttack(skill, maxRange, cellCount)) return 'melee';

    /* Nothing travels to a blast you are standing in: it happens where you are. */
    if (maxRange <= 2 && footprint >= BLAST_CELLS) return 'field';

    /* Several charges are several crossings: a line drawn again, not a body thrown. */
    if (charges > 1) return 'shot';

    if (footprint >= WIDE_CELLS) {
      return GROUND_WORDS.test(skill.name || '') ? 'upheaval' : 'skyfall';
    }

    return cellCount <= 1 ? 'shot' : 'projectile';
  }

  if (maxRange <= 0) return 'field';
  return cellCount <= 1 ? 'shot' : 'field';
}

/**
 * The shape the cells make, named so the merged geometry knows what it is building.
 *
 * Derived from the resolved cells rather than from the skill, because the same
 * skill aimed differently covers a different shape — and because in a fight these
 * cells came from the server, which has no idea what a "cone" is.
 */
export function footprintOf(cells, { caster = null, target = null } = {}) {
  if (!cells.length) return 'single';
  if (cells.length === 1) return 'single';

  let minCol = Infinity;
  let maxCol = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;
  cells.forEach(({ col, row }) => {
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
  });
  const width = maxCol - minCol + 1;
  const height = maxRow - minRow + 1;
  const area = width * height;
  const fill = cells.length / area;

  if (width === 1 || height === 1) return 'line';

  // A filled box or disc around the target reads as a burst; a partly filled one
  // that grows away from the caster reads as a cone.
  if (caster && target && fill < 0.85) {
    const towardCol = Math.sign(target.col - caster.col);
    const towardRow = Math.sign(target.row - caster.row);
    if (towardCol || towardRow) {
      const near = cells.filter(c => (
        towardCol ? c.col === (towardCol > 0 ? minCol : maxCol) : c.row === (towardRow > 0 ? minRow : maxRow)
      )).length;
      const far = cells.filter(c => (
        towardCol ? c.col === (towardCol > 0 ? maxCol : minCol) : c.row === (towardRow > 0 ? maxRow : minRow)
      )).length;
      if (far > near + 1) return 'cone';
    }
  }

  /*
   * The hole is checked BEFORE the fill. A 3x3 rim is eight cells out of nine, so
   * by area it looks as solid as a burst — and calling it one closes the middle,
   * which is the one thing a ring must never do.
   */
  const inner = cells.filter(({ col, row }) => (
    col > minCol && col < maxCol && row > minRow && row < maxRow
  )).length;
  if (width >= 3 && height >= 3 && inner === 0) return 'ring';

  if (fill > 0.7) return 'burst';

  /*
   * A disc is not a square, so measuring it against its bounding box under-reads it:
   * the authored 5-cell plus and 13-cell diamond are exactly the cells `burst` draws,
   * yet they fill only half their box and would fall out as clusters. Measure them
   * against the disc they actually are — every cell within the outermost one — and a
   * round footprint reads round however the matrix was written.
   */
  const centreCol = (minCol + maxCol) / 2;
  const centreRow = (minRow + maxRow) / 2;
  let radius = 0;
  cells.forEach(({ col, row }) => {
    radius = Math.max(radius, Math.hypot(col - centreCol, row - centreRow));
  });
  let discCells = 0;
  for (let col = minCol; col <= maxCol; col++) {
    for (let row = minRow; row <= maxRow; row++) {
      if (Math.hypot(col - centreCol, row - centreRow) <= radius + 1e-6) discCells++;
    }
  }
  if (discCells && cells.length / discCells > 0.85) return 'burst';

  return 'cluster';
}

/**
 * The third axis: WHO cast it.
 *
 * Violation says what an effect is made of and intent says what it is doing, and
 * between them an Energy bolt out of Cybernetic Enhancements and an Energy bolt out
 * of Pure Energy are the same pixels. In this world they are not the same act. One
 * is a machine discharging on command; the other is a person pulling something
 * through themselves. The material is identical and the hand is not.
 *
 * The axis is the game's own — `TheChosenPath`, `apps/core/models/core.py` — and it
 * is deliberately the QUIETEST of the three. It may not change what a thing is made
 * of and it may not change what the thing is doing; it only changes how the same
 * material behaves in the same hand:
 *
 *   john (Technomancers)  engineered. Emitted, not summoned: the delivery is quick
 *                         and clipped, the recoil belongs to a mechanism, the debris
 *                         is sparse, fast and bright, and the ground it claims is a
 *                         clean figure trimmed to the cells rather than a softened
 *                         blot.
 *   json (Deviants)       channelled. Drawn through the world: it takes longer to
 *                         arrive, nothing recoils because nothing fired, and it
 *                         leaves more, slower, broader matter behind it.
 *
 * Numbers, not new pictures, on purpose — a path may never make an effect
 * unrecognisable as its own violation.
 */
export const PATHS = {
  john: {
    traceScale: 0.82, arrivalScale: 0.9, kick: 1.35, shake: 1.0,
    weight: 0.88, intensity: 0.06, crisp: true,
  },
  json: {
    traceScale: 1.18, arrivalScale: 1.12, kick: 0.7, shake: 1.06,
    weight: 1.12, intensity: -0.04, crisp: false,
  },
};

/* Nothing may run long enough to be waited on. Trace plus arrival, in ms. */
const MAX_CAST_MS = 2600;

const PATH_NAMES = {
  'path of john': 'john',
  'path of json': 'json',
  john: 'john',
  json: 'json',
  tech_john: 'john',
  magic_json: 'json',
};

/**
 * School to path, by name.
 *
 * The M2M in `apps/school/models.py` is the authority and `registerSchoolPaths`
 * below takes it whenever a host has the catalogue to hand. This table is the same
 * answer keyed by the thing that survives a database rebuild — `Basic` is absent
 * because it belongs to BOTH paths, and Weapons, Artefacts and Game Master because
 * they belong to neither; all four correctly resolve to no path and no modulation.
 */
const SCHOOL_PATH_BY_NAME = {
  'cybernetic enhancements school': 'john',
  'robototehniche school': 'john',
  'defensive systems school': 'john',
  'medtech school': 'john',
  'buffertech school': 'john',
  'combat augmentation school': 'john',
  'techno-mysticism school': 'john',
  'fireartsschool': 'json',
  'fire arts school': 'json',
  'water school': 'json',
  'blood school': 'json',
  'dark school': 'json',
  'light school': 'json',
  'mirage school': 'json',
  'pureenergy school': 'json',
  'pure energy school': 'json',
  'runic school of magic': 'json',
  'summon school': 'json',
  'heal school': 'json',
  'constructor school': 'json',
};

/**
 * The same table keyed by the fixture's own primary keys.
 *
 * A skill reaches this module carrying `school` as a bare uuid (see
 * `LearnedSchoolSerializer` — the client resolves it through the cached catalogue,
 * which this module must not import: pulling the API client into the module graph of
 * every board that draws an effect is the coupling `violation.js` documents at
 * length). These uuids are written into `apps/school/fixtures/schools_complete_with_paths.json`
 * rather than generated, so they are the same in every install loaded from it, and
 * they let the axis work with no wiring at all. A database whose schools were made
 * by hand simply falls through to no path — today's rendering — until a host calls
 * `registerSchoolPaths`.
 */
const SCHOOL_PATH_BY_ID = {
  '4f9cc24a-52f3-43a3-8e8b-b01a367a504f': 'john',
  '8a7ca417-1cfa-4369-9eaf-c6a6bc433f01': 'john',
  '7a7dbdf0-eb61-44df-9ff6-6a099bb0af86': 'john',
  'b125adff-1ee0-44f9-bb81-3298c5315001': 'john',
  'aa531c7f-97ce-4fba-8752-84c90b7cbfdb': 'john',
  '684821f8-8cb1-4a70-8273-23a9adcee28d': 'john',
  'de37c984-de4e-437d-ab0a-197fd6eed4c6': 'john',
  '43607b12-9f49-4993-99ba-a416daa8da10': 'json',
  '2be5bff8-4d58-45b9-b995-49a59950a3ac': 'json',
  '8743f3ee-a8cd-490e-a462-8bb14a5471d8': 'json',
  '700de899-6ea2-4afd-a9c8-a8c6386a7470': 'json',
  'b62dfea2-3e38-47f3-989b-5c075001b54d': 'json',
  '962bd011-7908-414f-bdd5-083db28ee763': 'json',
  'bb92d487-102d-462c-acf5-d176a26f9ad0': 'json',
  '85250085-fd4f-4f21-ba12-89b03f4ebdc7': 'json',
  '35d4b270-14f3-42fb-83ab-6cb505c8005d': 'json',
  '6a55bc8c-d469-4f67-9842-c59a09906864': 'json',
  '0896047b-d72d-44cd-9e8c-7653521abe0c': 'json',
};

let registeredSchoolPaths = null;

function normalizePathName(value) {
  if (!value) return null;
  return PATH_NAMES[String(value).trim().toLowerCase()] || null;
}

/**
 * Teach this module the live school-to-path map.
 *
 * Takes the paths catalogue the client already holds — `SkillService.getAllPaths()`,
 * `OpenaiPathWithSchools`: each path with its schools nested — and overrides both
 * tables above. A school in both paths is registered as neither, which is what
 * `Basic` is and what it should render as.
 */
export function registerSchoolPaths(paths) {
  if (!Array.isArray(paths)) return;
  const map = new Map();
  paths.forEach(entry => {
    const path = normalizePathName(entry?.name);
    if (!path) return;
    (entry.schools || []).forEach(school => {
      const key = school?.id || school;
      if (!key) return;
      map.set(key, map.has(key) && map.get(key) !== path ? null : path);
    });
  });
  registeredSchoolPaths = map.size ? map : null;
}

/**
 * Which path cast this, or null when nothing says.
 *
 * Every source is read for what it actually is: a path named on the skill, a nested
 * school object, or the bare school key the API really sends.
 */
export function pathOf(ability) {
  const skill = skillOf(ability);
  if (!skill) return null;

  const named = normalizePathName(skill.path || skill.school?.path?.name || skill.school?.path);
  if (named) return named;

  const school = skill.school;
  const key = school?.id || (typeof school === 'string' ? school : null);
  if (key) {
    if (registeredSchoolPaths?.has(key)) return registeredSchoolPaths.get(key);
    if (SCHOOL_PATH_BY_ID[key]) return SCHOOL_PATH_BY_ID[key];
  }

  const name = school?.name || skill.school_name;
  return name ? (SCHOOL_PATH_BY_NAME[String(name).trim().toLowerCase()] || null) : null;
}

/**
 * The path applied to one cast's numbers.
 *
 * `mergeMode` moves only when the host asked for the board's own `rounded-cells` —
 * the mode that softens the authoritative cell union with noise. `clipped-shape` is
 * that union intersected with the skill's ideal figure: still never more ground than
 * the server said, still a soft radial gradient with no outline anywhere, but a
 * cleaner edge, which is what an engineered effect should have. A bench asking for
 * `ideal-shape` is asking to see the shape itself, and the path does not argue.
 */
function applyPath(cast, path) {
  const profile = PATHS[path];
  if (!profile) return cast;
  const traceMs = Math.round(cast.traceMs * profile.traceScale);
  const arrivalMs = Math.round(cast.arrivalMs * profile.arrivalScale);
  const overrun = Math.max(0, (traceMs + arrivalMs) - MAX_CAST_MS);
  const weight = Math.min(1, cast.weight * profile.weight);
  return {
    ...cast,
    path,
    traceMs: overrun ? Math.max(0, traceMs - overrun) : traceMs,
    arrivalMs,
    casterKick: cast.casterKick * profile.kick,
    targetShake: cast.targetShake * profile.shake,
    weight,
    intensity: Math.min(1, Math.max(0.15, cast.intensity + profile.intensity)),
    impactForce: cast.impactForce * profile.shake,
    mergeMode: profile.crisp && cast.mergeMode === 'rounded-cells' ? 'clipped-shape' : cast.mergeMode,
  };
}

/**
 * The whole presentation of one use of one skill.
 *
 * @param ability  the skill, or an item/special carrying one
 * @param aim      the resolved aim: cells, caster, target
 * @param options  render defaults (battle or sandbox), intensity and seed
 */
export function castPresentation(ability, aim, options = {}) {
  const {
    caster = null,
    target = null,
    cells = [],
  } = aim || {};
  const {
    defaults = BATTLE_RENDER_DEFAULTS,
    intensity = null,
    seed = 1,
    id = null,
    impact = null,
  } = options;

  const skill = skillOf(ability);
  /*
   * The gap this cast actually has to cover, in cells, and the reason the delivery
   * is chosen after it: whether a body can arrive is a fact about this cast, not
   * about the skill. Chebyshev, because that is the distance a token moves.
   */
  const reach = caster && target
    ? Math.max(Math.abs(target.col - caster.col), Math.abs(target.row - caster.row))
    : null;
  const delivery = deliveryFor(ability, { cellCount: cells.length, reach });
  const profile = DELIVERIES[delivery];
  const footprintHint = footprintOf(cells, { caster, target });
  const intent = intentFor(ability, impact);
  const intentProfile = INTENTS[intent];
  const spectrum = wardSpectrum(ability, impact);
  /*
   * A ward against seven materials is made of none of them in particular, so the
   * ground, the light and the motes are drawn in the unattributed profile and only
   * the shell carries the seven. Picking `impact[0]` here instead would say the
   * shield is an Energy shield because Energy happens to be the fixture's first row.
   */
  const violationType = spectrum.length ? 'None' : skillViolation(ability, impact);

  /*
   * A spark and a fireball are the same renderer at different weights. Size comes
   * from the ground covered, not from the skill's name, so a one-cell hit is a
   * small bright event and a nine-cell one is a slow heavy one — and the grade
   * nudges it, because a rank-8 bolt should not look like a rank-1 bolt.
   */
  /*
   * A blow closes real ground. "Phantom Strike" is authored at 2-8 cells, and a
   * commit that crosses eight of them in the 240ms a shoulder-barge takes reads as a
   * teleport — so the charge is stretched by the gap it actually has to cover.
   */
  const traceMs = delivery === 'melee'
    ? Math.round(profile.traceMs * (1 + Math.min(5, Math.max(0, (reach ?? 1) - 1)) * 0.17))
    : profile.traceMs;

  const span = Math.max(1, Math.round(Math.sqrt(Math.max(1, cells.length))));
  const grade = Number(skill?.grade || 0);
  const weight = Math.min(1, (cells.length / 12) * 0.7 + (grade / 10) * 0.3);

  return applyPath({
    id: id || `cast-${seed}`,
    ability: skill,
    sprite: skill?.icon || null,
    name: skill?.name || 'Unnamed',
    violationType,
    spectrum,
    intent,
    intentProfile,
    returnsToCaster: returnsToCaster(ability, impact),
    delivery,
    ...profile,
    traceMs,
    footprintHint,
    cells,
    caster,
    target,
    origin: target || caster,
    size: span,
    weight,
    intensity: intensity ?? Math.min(1, 0.45 + weight * 0.55),
    seed,
    path: null,
    mergeMode: defaults.mergeMode,
    clipToPassable: defaults.clipToPassable,
    /* How hard the board is hit, which is what the tokens and plinths shake by. */
    impactForce: (profile.targetShake || 0) * (0.5 + weight),
  }, pathOf(ability));
}
